import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import CareLineLayout from "../components/careline/CareLineLayout";
import { poliService, tenantApi, clinicService } from "../services/api";

// ─── Icons ────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-6 h-6"
    >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const ClockIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-3.5 h-3.5"
    >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const QueueIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-3.5 h-3.5"
    >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const ActiveDotIcon = () => (
    <svg viewBox="0 0 8 8" className="w-2 h-2">
        <circle cx="4" cy="4" r="4" fill="#10b981" />
    </svg>
);

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
const SkeletonCard = () => (
    <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.07),0_1px_4px_rgba(15,23,42,0.04)] flex flex-col pointer-events-none">
        <div className="w-[52px] h-[52px] rounded-2xl mb-5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
        <div className="h-3 rounded-md mb-2.5 w-4/5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
        <div className="h-3 rounded-md mb-2.5 w-3/5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
        <div className="h-3 rounded-md mb-6 w-2/5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
        <div className="mt-6 flex justify-between">
            <div className="h-3 rounded-md w-2/5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
            <div className="h-3 rounded-md w-2/5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
        </div>
    </div>
);

// ─── Poli Card ────────────────────────────────────────────────────────────────
const PoliCard = ({ poli, index }) => (
    <article
        className="group relative bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.07),0_1px_4px_rgba(15,23,42,0.04)] flex flex-col overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:shadow-[0_12px_40px_rgba(6,182,212,0.18),0_4px_12px_rgba(15,23,42,0.08)] hover:-translate-y-1 hover:border-cyan-100 animate-[cardIn_0.5s_ease_both]"
        style={{
            padding: "clamp(24px, 3vw, 32px)",
            animationDelay: `${index * 80}ms`,
        }}
    >
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-400 to-teal-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] origin-left" />

        {/* Status badge */}
        <div className="inline-flex items-center gap-1.5 font-[DM_Sans] text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-[3px] w-fit mb-5 tracking-[0.03em]">
            <ActiveDotIcon />
            <span>Aktif</span>
        </div>

        {/* Icon */}
        <div className="relative w-fit mb-5">
            <div className="w-[52px] h-[52px] bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center text-white relative z-10 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-[1.08] group-hover:-rotate-3">
                <PlusIcon />
            </div>
            <div className="absolute inset-[-4px] bg-gradient-to-br from-cyan-400 to-teal-500 rounded-[20px] opacity-0 blur-[10px] z-0 transition-opacity duration-[400ms] ease-linear group-hover:opacity-35" />
        </div>

        {/* Title */}
        <h3
            className="font-[Plus_Jakarta_Sans] font-semibold text-slate-900 leading-[1.25] mb-2.5"
            style={{ fontSize: "clamp(1.1rem, 2vw, 1.3rem)" }}
        >
            {poli.nama_poli}
        </h3>

        {/* Description */}
        <p className="font-[DM_Sans] text-[0.8125rem] text-slate-400 leading-[1.65] font-light flex-1">
            {poli.deskripsi ||
                "Pemeriksaan kesehatan dan konsultasi dengan dokter spesialis berpengalaman."}
        </p>

        {/* Divider */}
        <div className="h-px bg-slate-100 my-5" />

        {/* Meta */}
        <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-[7px] font-[DM_Sans] text-xs text-cyan-700">
                <ClockIcon />
                <span className="text-slate-400 font-normal flex-1">
                    Jam Operasional
                </span>
                <span className="text-slate-700 font-semibold bg-slate-100 px-2.5 py-[3px] rounded-full text-[11px]">
                    {poli.jam_operasional || "—"}
                </span>
            </div>
            <div className="flex items-center gap-[7px] font-[DM_Sans] text-xs text-cyan-700">
                <QueueIcon />
                <span className="text-slate-400 font-normal flex-1">
                    Antrian Saat Ini
                </span>
                <span className="text-slate-700 font-semibold bg-slate-100 px-2.5 py-[3px] rounded-full text-[11px]">
                    0 orang
                </span>
            </div>
        </div>
    </article>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Polyclinics = () => {
    const { slug } = useParams();
    const isTenant = Boolean(slug);
    const [polyclinics, setPolyclinics] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [clinicBrand, setClinicBrand] = useState(null);
    const [tenantError, setTenantError] = useState(false);

    useEffect(() => {
        if (!isTenant) {
            setClinicBrand(null);
            setTenantError(false);
            return;
        }
        let cancelled = false;
        clinicService
            .getBySlug(slug)
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success) setClinicBrand(res.data.data);
                else setTenantError(true);
            })
            .catch(() => {
                if (!cancelled) setTenantError(true);
            });
        return () => {
            cancelled = true;
        };
    }, [isTenant, slug]);

    useEffect(() => {
        const fetchPolyclinics = async () => {
            try {
                setIsLoading(true);
                const response = isTenant
                    ? await tenantApi(slug).poli.getAll()
                    : await poliService.getAll();
                if (response.data?.success) {
                    const rows = response.data.data || [];
                    const activePoli = rows.filter(
                        (p) => !p.status || p.status === "aktif",
                    );
                    setPolyclinics(activePoli);
                }
            } catch (error) {
                console.error("Error fetching polyclinics:", error);
                if (isTenant) setTenantError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPolyclinics();
    }, [isTenant, slug]);

    if (isTenant && tenantError) {
        return (
            <CareLineLayout slug={slug} activePage="polyclinic" logo={null} primaryColor="#06B6D4">
                <div className="flex flex-1 min-h-[50vh] flex-col items-center justify-center px-6 text-center font-jakarta">
                    <p className="text-slate-600 mb-4">Klinik tidak ditemukan atau tidak dapat dimuat.</p>
                    <Link
                        to={`/${slug}`}
                        className="text-sm font-semibold text-[#06B6D4] hover:underline"
                    >
                        Kembali ke beranda klinik
                    </Link>
                </div>
            </CareLineLayout>
        );
    }

    return (
        <CareLineLayout
            activePage="polyclinic"
            slug={isTenant ? slug : null}
            logo={clinicBrand?.logo_url}
            primaryColor={clinicBrand?.primary_color}
        >
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

                @keyframes shimmer {
                    0%   { background-position: -600px 0; }
                    100% { background-position:  600px 0; }
                }
                @keyframes cardIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* Hero fade + slide up animations */
                @keyframes heroFadeUp {
                    from { opacity: 0; transform: translateY(28px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                .hero-title {
                    animation: heroFadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both;
                }
                .hero-sub {
                    animation: heroFadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.3s both;
                }
            `}</style>

            {/* ── Hero ── */}
            <section
                className="relative flex flex-col items-center justify-center overflow-hidden bg-slate-900"
                style={{
                    height: "clamp(280px, 42vw, 460px)",
                    backgroundImage:
                        "url('/careline/careline-polyclinics-hero.svg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/25 to-slate-900/55" />

                <div className="relative z-10 text-center px-6">
                    <h1
                        className="hero-title font-[Plus_Jakarta_Sans] font-extrabold text-white leading-[1.1] tracking-[-0.01em]"
                        style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}
                    >
                        Daftar{" "}
                        <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#a5f3fc] via-[#67e8f9] to-[#22d3ee]">
                            Poliklinik
                        </em>{" "}
                        Kami
                    </h1>
                    <p
                        className="hero-sub font-[DM_Sans] font-light text-white/75 max-w-[480px] mx-auto mt-4"
                        style={{ fontSize: "clamp(0.875rem, 1.5vw, 1rem)" }}
                    >
                        Temukan layanan spesialis terbaik untuk kebutuhan
                        kesehatan Anda dan keluarga.
                    </p>
                </div>

                {/* Wave */}
                <div className="absolute bottom-[-1px] left-0 w-full z-10 leading-none">
                    <svg
                        viewBox="0 0 1440 56"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="block w-full h-14"
                    >
                        <path
                            d="M0,32 C360,56 1080,0 1440,32 L1440,56 L0,56 Z"
                            fill="#f8fafc"
                        />
                    </svg>
                </div>
            </section>

            {/* ── Grid Section ── */}
            <section
                className="relative bg-slate-50"
                style={{
                    padding:
                        "clamp(48px, 8vw, 96px) 0 clamp(64px, 10vw, 120px)",
                    backgroundImage:
                        "url('/patterns/careline-polyclinics-pattern.svg')",
                    backgroundSize: "auto",
                    backgroundPosition: "center",
                }}
            >
                {/* Section header */}
                <div
                    className="text-center px-4"
                    style={{ marginBottom: "clamp(36px, 5vw, 64px)" }}
                >
                    <h2
                        className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 leading-[1.15] tracking-[-0.02em]"
                        style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}
                    >
                        Spesialis yang{" "}
                        <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                            tersedia
                        </em>
                    </h2>
                    <p className="font-[DM_Sans] text-[0.9375rem] text-slate-500 font-light max-w-[420px] mx-auto mt-3">
                        Semua poliklinik kami dilayani oleh tenaga medis
                        berpengalaman dan berlisensi.
                    </p>
                </div>

                {/* Grid */}
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mx-auto"
                    style={{
                        gap: "24px",
                        maxWidth: "1200px",
                        padding: "0 clamp(16px, 4vw, 48px)",
                    }}
                >
                    {isLoading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))
                    ) : polyclinics.length === 0 ? (
                        <div className="col-span-3 text-center py-20 px-6">
                            <p className="font-[Plus_Jakarta_Sans] text-xl font-semibold text-slate-700 mb-2">
                                Belum ada poliklinik
                            </p>
                            <p className="font-[DM_Sans] text-sm text-slate-400">
                                Belum ada poliklinik yang tersedia saat ini.
                                Silakan cek kembali nanti.
                            </p>
                        </div>
                    ) : (
                        polyclinics.map((poli, index) => (
                            <PoliCard
                                key={poli.id_poli}
                                poli={poli}
                                index={index}
                            />
                        ))
                    )}
                </div>
            </section>
        </CareLineLayout>
    );
};

export default Polyclinics;
